'use client';

import { FONT_TEXT } from '@/app/fonts';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { ShareableChart } from '../ShareableChart';

const data = [
  { name: 'Referrals', low: 0, high: 0, range: '$0', color: '#10b981', category: 'Free' },
  { name: 'Organic/SEO', low: 0, high: 0, range: '$0', color: '#10b981', category: 'Free' },
  // First-party cost is per loan product, never per channel (source of truth §3, 2026-09-22): purchase $30–$60.
  { name: 'First-Party Exclusive (purchase)', low: 30, high: 60, range: '$30–$60', color: '#ff34a7', category: 'First-Party' },
  { name: 'LendingTree', low: 30, high: 100, range: '$30–$100', color: '#94a3b8', category: 'Third-Party' },
  { name: 'Zillow', low: 75, high: 150, range: '$75–$150', color: '#94a3b8', category: 'Third-Party' },
  { name: 'Bankrate', low: 100, high: 250, range: '$100–$250', color: '#94a3b8', category: 'Third-Party' },
  { name: 'NerdWallet', low: 160, high: 195, range: '$160–$195', color: '#94a3b8', category: 'Third-Party' },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: typeof data[0] }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
      <p className="font-semibold text-sm text-gray-900">{item.name}</p>
      <p className="text-sm text-gray-600">
        CPL: <span className="font-medium">{item.range}</span>
      </p>
      <p className="text-xs text-gray-400 mt-1">{item.category}</p>
    </div>
  );
}

interface CustomBarLabelProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  value?: number;
  index?: number;
}

function CustomBarLabel({ x = 0, y = 0, width = 0, height = 0, index = 0 }: CustomBarLabelProps) {
  const item = data[index];
  if (!item) return null;
  
  const labelX = x + width + 8;
  const labelY = y + height / 2;
  
  return (
    <text
      x={labelX}
      y={labelY}
      fill="#374151"
      fontSize={12}
      fontWeight={500}
      dominantBaseline="middle"
      fontFamily={FONT_TEXT}
    >
      {item.range}
    </text>
  );
}

export function CPLComparisonChart() {
  return (
    <ShareableChart title="Cost Per Lead by Source 2026">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: FONT_TEXT }}>
          Cost Per Lead by Source (2026)
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Lower is better — but conversion rate matters too
        </p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4" style={{ fontFamily: FONT_TEXT }}>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#10b981' }} />
          <span className="text-xs text-gray-600">Free / Organic</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#ff34a7' }} />
          <span className="text-xs text-gray-600">First-Party Exclusive (your own marketing)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#94a3b8' }} />
          <span className="text-xs text-gray-600">Third-Party Aggregator</span>
        </div>
      </div>

      <div className="w-full" style={{ height: '400px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 80, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 280]}
              tick={{ fontSize: 11, fill: '#6b7280', fontFamily: FONT_TEXT }}
              tickFormatter={(value: number) => `$${value}`}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={130}
              tick={{ fontSize: 12, fill: '#374151', fontFamily: FONT_TEXT }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
            <Bar dataKey="high" radius={[0, 4, 4, 0]} barSize={28} label={<CustomBarLabel />}>
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: FONT_TEXT }}>
        Source: leadpops.com · Industry data, HousingWire (2025–2026)
      </p>
    </ShareableChart>
  );
}
